import React, { useState, useEffect } from 'react';
import RSVPForm from './RSVPForm';
import VinylPlayer from './VinylPlayer';
import { Calendar, MapPin, Clock, Camera, Heart, Utensils, ChevronLeft, ChevronRight } from 'lucide-react';

// Icon mapping for timeline
const iconMap = {
  camera: Camera,
  heart: Heart,
  utensils: Utensils,
  clock: Clock,
};

// Hero Layout Components
const HeroBanner = ({ photos, style }) => {
  if (!photos || photos.length === 0) return null;
  return (
    <div className="mb-6 -mx-4">
      <div 
        className="relative w-full h-48 md:h-64 overflow-hidden"
      >
        <img 
          src={photos[0]} 
          alt="Hero"
          className="w-full h-full object-cover"
        />
      </div>
    </div>
  );
};

const HeroFullWidth = ({ photos, style }) => {
  if (!photos || photos.length === 0) return null;
  return (
    <div className="mb-6 -mx-4">
      <div className="relative w-full overflow-hidden">
        <img 
          src={photos[0]} 
          alt="Couple"
          className="w-full aspect-[4/5] object-cover"
        />
      </div>
    </div>
  );
};

const HeroSideBySide = ({ photos, style }) => {
  if (!photos || photos.length === 0) return null;
  return (
    <div className="mb-6 -mx-4">
      <div className="flex gap-1">
        {photos.slice(0, 2).map((photo, idx) => (
          <div key={idx} className="flex-1">
            <img 
              src={photo} 
              alt={`Photo ${idx + 1}`}
              className="w-full aspect-[3/4] object-cover"
            />
          </div>
        ))}
        {photos.length === 1 && (
          <div className="flex-1 bg-gray-100 flex items-center justify-center aspect-[3/4]">
            <span className="text-gray-400 text-sm">Photo 2</span>
          </div>
        )}
      </div>
    </div>
  );
};

const HeroSquares = ({ photos, style }) => {
  if (!photos || photos.length === 0) return null;
  return (
    <div className="mb-6 -mx-4">
      <div className="grid grid-cols-2 gap-1">
        {[0, 1, 2, 3].map((idx) => (
          <div key={idx}>
            {photos[idx] ? (
              <img 
                src={photos[idx]} 
                alt={`Photo ${idx + 1}`}
                className="w-full aspect-square object-cover"
              />
            ) : (
              <div className="w-full aspect-square bg-gray-100 flex items-center justify-center">
                <span className="text-gray-400 text-xs">Photo {idx + 1}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const HeroSlideshow = ({ photos, accentColor }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  useEffect(() => {
    if (!photos || photos.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % photos.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [photos?.length]);

  if (!photos || photos.length === 0) return null;

  return (
    <div className="mb-6 -mx-4">
      <div className="relative">
        <div className="relative aspect-[4/5] overflow-hidden">
            {photos.map((photo, idx) => (
              <img 
                key={idx}
                src={photo} 
                alt={`Slide ${idx + 1}`}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                  idx === currentIndex ? 'opacity-100' : 'opacity-0'
                }`}
              />
            ))}
          </div>
        {/* Navigation dots */}
        {photos.length > 1 && (
          <div className="flex justify-center gap-2 mt-4">
            {photos.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  idx === currentIndex ? '' : 'opacity-40'
                }`}
                style={{ backgroundColor: accentColor }}
              />
            ))}
          </div>
        )}
        {/* Arrow buttons */}
        {photos.length > 1 && (
          <>
            <button
              onClick={() => setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length)}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-gray-50"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
            <button
              onClick={() => setCurrentIndex((prev) => (prev + 1) % photos.length)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-gray-50"
            >
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const HeroMarquee = ({ photos, style }) => {
  if (!photos || photos.length === 0) return null;
  
  // Duplicate photos for seamless loop
  const duplicatedPhotos = [...photos, ...photos];
  
  return (
    <div className="mb-6 -mx-4 overflow-hidden">
      <div 
        className="flex gap-1 animate-marquee"
        style={{
          animation: 'marquee 20s linear infinite',
          width: `${duplicatedPhotos.length * 130}px`
        }}
      >
        {duplicatedPhotos.map((photo, idx) => (
          <div 
            key={idx}
            className="flex-shrink-0"
            style={{ width: '120px' }}
          >
            <img 
              src={photo} 
              alt={`Photo ${idx + 1}`}
              className="w-full aspect-[3/4] object-cover"
            />
          </div>
        ))}
      </div>
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
};

const HeroTextOnly = ({ coupleNames, storyText, primaryColor, accentColor }) => {
  return (
    <div className="mb-10 text-center py-8">
      <div className="flex items-center justify-center gap-3 mb-6">
        <div className="w-16 h-px" style={{ backgroundColor: accentColor }} />
        <Heart className="w-5 h-5" style={{ color: accentColor }} />
        <div className="w-16 h-px" style={{ backgroundColor: accentColor }} />
      </div>
    </div>
  );
};

const HeroSinglePage = ({ photos, coupleNames, primaryColor, accentColor }) => {
  if (!photos || photos.length === 0) return null;
  
  return (
    <div className="mb-10 -mx-6 -mt-12">
      <div className="relative min-h-[50vh] flex items-center justify-center">
        <img 
          src={photos[0]} 
          alt="Hero"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(0.7)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/50" />
        <div className="relative z-10 text-center px-6 py-12">
          <p 
            className="text-xs tracking-[0.4em] uppercase mb-4 text-white/80"
          >
            Wedding Invitation
          </p>
          <h1 
            className="text-3xl md:text-4xl font-light tracking-wider text-white mb-4"
            style={{ textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}
          >
            {coupleNames}
          </h1>
          <div className="flex items-center justify-center gap-3">
            <div className="w-12 h-px bg-white/60" />
            <Heart className="w-4 h-4" style={{ color: accentColor }} />
            <div className="w-12 h-px bg-white/60" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default function ClassicFormal({ 
  data,
  onSubmitRSVP,
  isSubmitting,
  submitSuccess,
  isPreview = false
}) {
  const {
    couple_names = 'John & Jane',
    event_date = '',
    event_time = '',
    venue_name = '',
    venue_address = '',
    story_text = '',
    timeline = [],
    hero_image_url = '',
    hero_layout = 'fullwidth',
    hero_photos = [],
    gallery_images = [],
    color_theme = {},
    font_style = 'serif',
    background_music_url = '',
    song_title = '',
    artist_name = '',
    vinyl_cover_image = ''
  } = data || {};

  const bgColor = color_theme.background || '#f5f5f0';
  const primaryColor = color_theme.primary || '#1a1a1a';
  const secondaryColor = color_theme.secondary || '#666666';
  const accentColor = color_theme.accent || '#d4af37';

  const fontClass = {
    serif: 'font-serif',
    sans: 'font-sans',
    script: 'font-serif'
  }[font_style] || 'font-serif';

  // Format date nicely
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        weekday: 'long',
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  // Get photos for hero - prioritize hero_photos, fall back to hero_image_url
  const getHeroPhotos = () => {
    if (hero_photos && hero_photos.length > 0) {
      return hero_photos.filter(Boolean);
    }
    if (hero_image_url) {
      return [hero_image_url];
    }
    return [];
  };

  const heroPhotos = getHeroPhotos();

  // Render hero based on layout
  const renderHero = () => {
    // For single page layout, render differently (full background)
    if (hero_layout === 'singlepage') {
      return (
        <HeroSinglePage 
          photos={heroPhotos}
          coupleNames={couple_names}
          primaryColor={primaryColor}
          accentColor={accentColor}
        />
      );
    }

    switch (hero_layout) {
      case 'banner':
        return <HeroBanner photos={heroPhotos} />;
      case 'sidebyside':
        return <HeroSideBySide photos={heroPhotos} />;
      case 'squares':
        return <HeroSquares photos={heroPhotos} />;
      case 'slideshow':
        return <HeroSlideshow photos={heroPhotos} accentColor={accentColor} />;
      case 'marquee':
        return <HeroMarquee photos={heroPhotos} />;
      case 'textonly':
        return <HeroTextOnly coupleNames={couple_names} storyText={story_text} primaryColor={primaryColor} accentColor={accentColor} />;
      case 'fullwidth':
      default:
        return <HeroFullWidth photos={heroPhotos} />;
    }
  };

  // For single page layout, use special structure
  if (hero_layout === 'singlepage') {
    return (
      <div 
        className={`min-h-screen ${fontClass}`}
        style={{ backgroundColor: bgColor }}
      >
        {renderHero()}
        
        <div className="max-w-lg mx-auto px-6 py-8">
          {/* Event Details */}
          <div className="text-center space-y-6 mb-10">
            {event_date && (
              <div className="flex items-center justify-center gap-3">
                <Calendar className="w-4 h-4" style={{ color: accentColor }} />
                <span style={{ color: primaryColor }}>{formatDate(event_date)}</span>
              </div>
            )}
            
            {event_time && (
              <div className="flex items-center justify-center gap-3">
                <Clock className="w-4 h-4" style={{ color: accentColor }} />
                <span style={{ color: primaryColor }}>{event_time}</span>
              </div>
            )}
            
            {(venue_name || venue_address) && (
              <div className="flex items-start justify-center gap-3">
                <MapPin className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: accentColor }} />
                <div className="text-left">
                  {venue_name && <p style={{ color: primaryColor }}>{venue_name}</p>}
                  {venue_address && <p className="text-sm" style={{ color: secondaryColor }}>{venue_address}</p>}
                </div>
              </div>
            )}
          </div>

          {/* Story */}
          {story_text && (
            <p 
              className="text-sm leading-relaxed max-w-md mx-auto italic text-center mb-10"
              style={{ color: secondaryColor }}
            >
              {story_text}
            </p>
          )}

          {/* Timeline */}
          {timeline && timeline.length > 0 && (
            <div className="mb-12">
              <p 
                className="text-center text-xs tracking-[0.2em] uppercase mb-6"
                style={{ color: accentColor }}
              >
                Timeline
              </p>
              <div className="space-y-4">
                {timeline.map((item, idx) => {
                  const IconComponent = iconMap[item.icon] || Clock;
                  return (
                    <div key={idx} className="flex items-center gap-4">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${accentColor}15` }}
                      >
                        <IconComponent className="w-4 h-4" style={{ color: accentColor }} />
                      </div>
                      <div className="flex-1 flex items-center justify-between">
                        <span style={{ color: primaryColor }}>{item.title}</span>
                        <span className="text-sm" style={{ color: secondaryColor }}>{item.time}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Gallery Images */}
          {gallery_images && gallery_images.length > 0 && (
            <div className="mb-10">
              <div className={`grid gap-3 mx-auto max-w-sm ${
                gallery_images.length === 1 ? 'grid-cols-1' :
                gallery_images.length === 2 ? 'grid-cols-2' :
                'grid-cols-3'
              }`}>
                {gallery_images.slice(0, 6).map((img, idx) => (
                  <div 
                    key={idx}
                    className="p-1"
                    style={{ backgroundColor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                  >
                    <img 
                      src={img} 
                      alt={`Gallery ${idx + 1}`}
                      className="w-full aspect-square object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Divider before RSVP */}
          <div className="flex items-center justify-center gap-4 my-10">
            <div className="flex-1 h-px" style={{ backgroundColor: `${accentColor}40` }} />
            <span 
              className="text-xs tracking-[0.2em] uppercase"
              style={{ color: accentColor }}
            >
              RSVP
            </span>
            <div className="flex-1 h-px" style={{ backgroundColor: `${accentColor}40` }} />
          </div>

          {/* RSVP Form */}
          {!isPreview && (
            <div 
              className="p-6 rounded-lg"
              style={{ backgroundColor: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}
            >
              <RSVPForm
                onSubmit={onSubmitRSVP}
                colorTheme={color_theme}
                fontStyle={font_style}
                isSubmitting={isSubmitting}
                submitSuccess={submitSuccess}
              />
            </div>
          )}

          {/* Preview mode indicator */}
          {isPreview && (
            <div 
              className="p-6 rounded-lg text-center"
              style={{ backgroundColor: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}
            >
              <p className="text-gray-400 text-sm">RSVP form will appear here</p>
            </div>
          )}

          {/* Footer */}
          <div className="text-center mt-12 pt-8 border-t" style={{ borderColor: `${accentColor}20` }}>
            <p className="text-xs" style={{ color: secondaryColor }}>
              We can't wait to celebrate with you!
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div 
      className={`min-h-screen ${fontClass}`}
      style={{ backgroundColor: bgColor }}
    >
      {/* Paper texture overlay - positioned absolutely so it doesn't affect content opacity */}
      <div className="min-h-screen relative">
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
            backgroundBlendMode: 'overlay',
            opacity: 0.03
          }}
        />
        <div className="relative max-w-lg mx-auto px-4 pt-2 pb-6">
          
          {/* Hero Section - Dynamic based on layout (AT THE VERY TOP) */}
          {renderHero()}
          
          {/* Header - show only for non-textonly layouts */}
          {hero_layout !== 'textonly' && heroPhotos.length === 0 && (
            <div className="text-center mb-8">
              <p 
                className="text-sm tracking-[0.3em] uppercase mb-3"
                style={{ color: accentColor }}
              >
                Wedding Invitation
              </p>
              
              {/* Decorative element */}
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-12 h-px" style={{ backgroundColor: accentColor }} />
                <Heart className="w-4 h-4" style={{ color: accentColor }} />
                <div className="w-12 h-px" style={{ backgroundColor: accentColor }} />
              </div>
            </div>
          )}

          {/* Gallery Images - Elegant Grid */}
          {gallery_images && gallery_images.length > 0 && (
            <div className="mb-10">
              <div className={`grid gap-3 mx-auto max-w-sm ${
                gallery_images.length === 1 ? 'grid-cols-1' :
                gallery_images.length === 2 ? 'grid-cols-2' :
                'grid-cols-3'
              }`}>
                {gallery_images.slice(0, 6).map((img, idx) => (
                  <div 
                    key={idx}
                    className="p-1"
                    style={{ backgroundColor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                  >
                    <img 
                      src={img} 
                      alt={`Gallery ${idx + 1}`}
                      className="w-full aspect-square object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Couple Names */}
          <div className="text-center mb-10">
            <h1 
              className="text-3xl md:text-4xl font-light tracking-wide mb-4"
              style={{ color: primaryColor }}
            >
              {couple_names}
            </h1>
            
            {/* Story/Quote */}
            {story_text && (
              <p 
                className="text-sm leading-relaxed max-w-md mx-auto italic"
                style={{ color: secondaryColor }}
              >
                {story_text}
              </p>
            )}
          </div>

          {/* Divider */}
          <div className="flex items-center justify-center gap-4 my-10">
            <div className="flex-1 h-px" style={{ backgroundColor: `${accentColor}40` }} />
            <span 
              className="text-xs tracking-[0.2em] uppercase"
              style={{ color: accentColor }}
            >
              Wedding Ceremony
            </span>
            <div className="flex-1 h-px" style={{ backgroundColor: `${accentColor}40` }} />
          </div>

          {/* Event Details */}
          <div className="text-center space-y-6 mb-10">
            {/* Date */}
            {event_date && (
              <div className="flex items-center justify-center gap-3">
                <Calendar className="w-4 h-4" style={{ color: accentColor }} />
                <span style={{ color: primaryColor }}>{formatDate(event_date)}</span>
              </div>
            )}
            
            {/* Time */}
            {event_time && (
              <div className="flex items-center justify-center gap-3">
                <Clock className="w-4 h-4" style={{ color: accentColor }} />
                <span style={{ color: primaryColor }}>{event_time}</span>
              </div>
            )}
            
            {/* Venue */}
            {(venue_name || venue_address) && (
              <div className="flex items-start justify-center gap-3">
                <MapPin className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: accentColor }} />
                <div className="text-left">
                  {venue_name && <p style={{ color: primaryColor }}>{venue_name}</p>}
                  {venue_address && <p className="text-sm" style={{ color: secondaryColor }}>{venue_address}</p>}
                </div>
              </div>
            )}
          </div>

          {/* Timeline */}
          {timeline && timeline.length > 0 && (
            <div className="mb-12">
              <p 
                className="text-center text-xs tracking-[0.2em] uppercase mb-6"
                style={{ color: accentColor }}
              >
                Timeline
              </p>
              <div className="space-y-4">
                {timeline.map((item, idx) => {
                  const IconComponent = iconMap[item.icon] || Clock;
                  return (
                    <div key={idx} className="flex items-center gap-4">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${accentColor}15` }}
                      >
                        <IconComponent className="w-4 h-4" style={{ color: accentColor }} />
                      </div>
                      <div className="flex-1 flex items-center justify-between">
                        <span style={{ color: primaryColor }}>{item.title}</span>
                        <span className="text-sm" style={{ color: secondaryColor }}>{item.time}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Vinyl Music Player */}
          {background_music_url && (
            <VinylPlayer
              audioSrc={background_music_url}
              coverImage={vinyl_cover_image || hero_photos?.[0] || hero_image_url}
              songTitle={song_title}
              artistName={artist_name}
              accentColor={accentColor}
              bgColor={bgColor}
              isPreview={isPreview}
            />
          )}

          {/* Divider before RSVP */}
          <div className="flex items-center justify-center gap-4 my-10">
            <div className="flex-1 h-px" style={{ backgroundColor: `${accentColor}40` }} />
            <span 
              className="text-xs tracking-[0.2em] uppercase"
              style={{ color: accentColor }}
            >
              RSVP
            </span>
            <div className="flex-1 h-px" style={{ backgroundColor: `${accentColor}40` }} />
          </div>

          {/* RSVP Form */}
          {!isPreview && (
            <div 
              className="p-6 rounded-lg"
              style={{ backgroundColor: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}
            >
              <RSVPForm
                onSubmit={onSubmitRSVP}
                colorTheme={color_theme}
                fontStyle={font_style}
                isSubmitting={isSubmitting}
                submitSuccess={submitSuccess}
              />
            </div>
          )}

          {/* Preview mode indicator */}
          {isPreview && (
            <div 
              className="p-6 rounded-lg text-center"
              style={{ backgroundColor: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}
            >
              <p className="text-gray-400 text-sm">RSVP form will appear here</p>
            </div>
          )}

          {/* Footer */}
          <div className="text-center mt-12 pt-8 border-t" style={{ borderColor: `${accentColor}20` }}>
            <p className="text-xs" style={{ color: secondaryColor }}>
              We can't wait to celebrate with you!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
